import type { OcrResult, OcrRuntimeParamsInput, PaddleOCR as PaddleOcrClass } from '@paddleocr/paddleocr-js'
import type { GamePreset } from '@/presets/types'
import { resolveCachedOcrModels, type ResolvedOcrModels } from '@/services/ocr-resource-cache'
import { classifyOcrError, OcrEngineFailure } from '@/core/ocr-recovery'
import { performanceDiagnostics } from '@/services/performance-diagnostics'
import { OcrInitializationError, OcrInitializationTimeoutError, type OcrStartupDiagnostic, type OcrStartupStage } from '@/services/ocr-startup'

export interface OcrReading {
  readonly text: string
  /** Full accepted OCR output when `text` has been narrowed to one metric candidate. */
  readonly fullText?: string
  readonly items: readonly OcrTextItem[]
  readonly confidence: number
  readonly durationMs: number
  readonly detectedItems: number
  readonly acceptedItems: number
  readonly bestConfidence: number
}

export interface OcrTextItem {
  readonly text: string
  readonly confidence: number
  readonly polygon: readonly (readonly [number, number])[]
}

type OcrEngineInstance = Awaited<ReturnType<typeof PaddleOcrClass.create>>
const DEFAULT_PREDICTION_TIMEOUT_MS = 12_000
// Dev worker bundles can take a long time to transfer over remote port forwarding.
// Zero disables the startup deadline; production builds retain the watchdog.
const DEFAULT_INITIALIZATION_TIMEOUT_MS = import.meta.env.DEV ? 0 : 60_000

interface InitializingEngine {
  readonly generation: number
  readonly promise: Promise<OcrEngineInstance>
  cancel(): void
}

class OcrEngineResetError extends Error {
  constructor() {
    super('OCR engine was reset')
    this.name = 'OcrEngineResetError'
  }
}

export class OcrPredictionTimeoutError extends Error {
  constructor(timeoutMs: number) {
    super(`OCR prediction timed out after ${timeoutMs}ms`)
    this.name = 'OcrPredictionTimeoutError'
  }
}

export class OcrEngine {
  private engine: OcrEngineInstance | null = null
  private initializing: InitializingEngine | null = null
  private pendingDisposal: Promise<void> | null = null
  private activeResources: ResolvedOcrModels | null = null
  private busyToken: symbol | null = null
  private generation = 0

  constructor(
    private readonly models: GamePreset['remoteResources']['ocrModels'],
    private readonly predictionTimeoutMs = DEFAULT_PREDICTION_TIMEOUT_MS,
    private readonly initializationTimeoutMs = DEFAULT_INITIALIZATION_TIMEOUT_MS,
  ) {}

  async warmup(): Promise<void> {
    await this.ensureEngine()
  }

  get busy(): boolean { return this.busyToken !== null }

  /** Optional work never initializes a model or reports errors to the statistics owner. */
  async recognizeAuxiliary(image: ImageData, options: Readonly<OcrRuntimeParamsInput> = {}): Promise<OcrReading | null> {
    if (this.busyToken || !this.engine) return null
    const token = Symbol('auxiliary-ocr'), engine = this.engine, generation = this.generation
    this.busyToken = token
    try {
      // Keep the shared worker's existing watchdog: a stuck auxiliary prediction must
      // not permanently starve level/EXP OCR, or release a still-running worker for reuse.
      const results = await this.withPredictionTimeout(Promise.resolve().then(() => engine.predict(image, options)), engine, generation)
      return generation === this.generation && results[0]
        ? this.toReading(results[0], 0, options.textRecScoreThresh ?? .35) : null
    } catch { return null }
    finally { if (this.busyToken === token) this.busyToken = null }
  }

  async recognize(image: ImageData, options: Readonly<OcrRuntimeParamsInput> = {}): Promise<OcrReading | null> {
    if (this.busyToken) return null
    const token = Symbol('ocr-prediction')
    const generation = this.generation
    this.busyToken = token
    try {
      const engine = await this.ensureEngine()
      if (generation !== this.generation) return null
      const startedAt = performance.now()
      const prediction = Promise.resolve().then(() => engine.predict(image, {
        textDetLimitSideLen: Math.max(image.width, image.height),
        textRecScoreThresh: 0.35,
        ...options,
      }))
      const results = await this.withPredictionTimeout(prediction, engine, generation)
        .catch((error: unknown) => {
          if (error instanceof OcrPredictionTimeoutError || generation !== this.generation) throw error
          const { code } = classifyOcrError(error)
          if (code === 'image-error' || code === 'cancelled') throw error
          throw new OcrEngineFailure()
        })
        .finally(() => performanceDiagnostics.measure('ocr', performance.now() - startedAt))
      const [result] = results
      if (generation !== this.generation) return null
      const scoreThreshold = options.textRecScoreThresh ?? options.text_rec_score_thresh ?? 0.35
      return result ? this.toReading(result, performance.now() - startedAt, scoreThreshold) : null
    } finally {
      if (this.busyToken === token) this.busyToken = null
    }
  }

  async dispose(): Promise<void> {
    await this.resetEngine()
  }

  async recover(): Promise<void> {
    if (this.engine || this.initializing) performanceDiagnostics.increment('ocrResets')
    await this.resetEngine()
  }

  private async resetEngine(): Promise<void> {
    const instance = this.engine
    const initialization = this.initializing
    const resources = this.activeResources
    this.generation += 1
    this.engine = null
    this.initializing = null
    this.activeResources = null
    this.busyToken = null
    initialization?.cancel()
    if (instance) {
      await this.queueDisposal(instance)
      resources?.release()
      return
    }
    await this.pendingDisposal
    resources?.release()
  }

  private retainResources(resources: ResolvedOcrModels | null): void {
    this.activeResources?.release()
    this.activeResources = resources
  }

  private ensureEngine(): Promise<OcrEngineInstance> {
    if (this.engine) return Promise.resolve(this.engine)
    const generation = this.generation
    if (this.initializing?.generation === generation) return this.initializing.promise

    const startedAt = Date.now()
    const diagnostic: OcrStartupDiagnostic = {
      startedAt: new Date(startedAt).toISOString(), elapsedMs: 0, timeoutMs: this.initializationTimeoutMs,
      outcome: 'initializing', error: null,
      stages: {
        sdk: { status: 'pending', elapsedMs: 0 },
        cache: { status: 'pending', elapsedMs: 0 },
        worker: { status: 'pending', elapsedMs: 0 },
      },
    }
    const stageStarts: Partial<Record<OcrStartupStage, number>> = {}
    let settled = false
    let instance: OcrEngineInstance | null = null
    let resources: ResolvedOcrModels | null = null
    let resolve!: (engine: OcrEngineInstance) => void
    let reject!: (error: unknown) => void
    const promise = new Promise<OcrEngineInstance>((done, fail) => { resolve = done; reject = fail })
    const snapshot = (): OcrStartupDiagnostic => {
      diagnostic.elapsedMs = Math.max(0, Date.now() - startedAt)
      for (const stage of ['sdk', 'cache', 'worker'] as const) {
        if (diagnostic.stages[stage].status === 'running') {
          diagnostic.stages[stage].elapsedMs = Math.max(0, Date.now() - stageStarts[stage]!)
        }
      }
      return structuredClone(diagnostic)
    }
    const publish = () => performanceDiagnostics.setOcrStartup(snapshot())
    const releaseResources = () => {
      const cached = resources
      resources = null
      cached?.release()
    }
    const finish = (error?: unknown) => {
      if (settled) return
      settled = true
      clearTimeout(timer)
      document.removeEventListener('visibilitychange', checkDeadline)
      window.removeEventListener('pageshow', checkDeadline)
      if (this.initializing === initialization) this.initializing = null
      const report = snapshot()
      if (error) {
        report.outcome = error instanceof OcrInitializationTimeoutError ? 'timeout'
          : error instanceof OcrEngineResetError ? 'cancelled' : 'error'
        report.error = {
          name: error instanceof Error ? error.name : 'Error',
          message: (error instanceof Error ? error.message : String(error)).replace(/(?:https?:\/\/|blob:)[^\s)]+/g, '[resource]').slice(0, 1_000),
        }
        // Reject with the original failure before terminating pending SDK requests.
        reject(error instanceof OcrEngineResetError ? error
          : error instanceof OcrInitializationTimeoutError ? new OcrInitializationTimeoutError(report)
            : new OcrInitializationError(report.error.message, report))
        if (instance) void this.safeDispose(instance)
        if (!(error instanceof OcrEngineResetError)) performanceDiagnostics.error('ocr-initialize-error')
        releaseResources()
      } else {
        report.outcome = 'ready'
        this.engine = instance!
        this.retainResources(resources)
        resources = null
        resolve(instance!)
      }
      performanceDiagnostics.setOcrStartup(report)
      performanceDiagnostics.measure('ocr-initialize', report.elapsedMs)
    }
    const checkDeadline = () => {
      if (this.initializationTimeoutMs > 0 && Date.now() - startedAt >= this.initializationTimeoutMs) finish(new OcrInitializationTimeoutError(snapshot()))
    }
    const timer = this.initializationTimeoutMs > 0
      ? setTimeout(() => finish(new OcrInitializationTimeoutError(snapshot())), this.initializationTimeoutMs)
      : undefined
    const initialization: InitializingEngine = { generation, promise, cancel: () => finish(new OcrEngineResetError()) }
    this.initializing = initialization
    document.addEventListener('visibilitychange', checkDeadline)
    window.addEventListener('pageshow', checkDeadline)
    publish()
    const assertActive = () => {
      checkDeadline()
      if (settled || generation !== this.generation) throw new OcrEngineResetError()
    }
    const stage = async <T>(name: OcrStartupStage, action: () => Promise<T>): Promise<T> => {
      assertActive()
      stageStarts[name] = Date.now()
      diagnostic.stages[name].status = 'running'
      publish()
      try {
        const result = await action()
        assertActive()
        diagnostic.stages[name] = { status: 'ready', elapsedMs: Math.max(0, Date.now() - stageStarts[name]!) }
        publish()
        return result
      } catch (error) {
        if (!settled) {
          diagnostic.stages[name] = { status: 'error', elapsedMs: Math.max(0, Date.now() - stageStarts[name]!) }
        }
        throw error
      }
    }
    void (async () => {
      try {
        await this.pendingDisposal
        assertActive()
        const [{ PaddleOCR }, cached] = await Promise.all([
          stage('sdk', () => this.loadSdk()),
          stage('cache', async () => {
            const cached = await resolveCachedOcrModels(this.models)
            // Own the lease even if the parallel SDK import never returns.
            if (settled) { cached.release(); throw new OcrEngineResetError() }
            resources = cached
            return cached
          }),
        ])
        await stage('worker', async () => {
          performanceDiagnostics.increment('ocrInitializations')
          const created = await PaddleOCR.create({
            worker: true,
            initialize: false,
            lang: 'ch',
            textDetectionModelName: cached.models.detection.name,
            textDetectionModelAsset: { url: cached.models.detection.url },
            textRecognitionModelName: cached.models.recognition.name,
            textRecognitionModelAsset: { url: cached.models.recognition.url },
            textDetectionBatchSize: 1,
            textRecognitionBatchSize: 1,
            ortOptions: {
              backend: 'wasm',
              wasmPaths: {
                mjs: cached.wasmPaths.mjs,
                wasm: cached.wasmPaths.wasm,
              },
              numThreads: 1,
              simd: true,
              proxy: false,
            },
          })
          if (settled) { void this.safeDispose(created); throw new OcrEngineResetError() }
          instance = created
          assertActive()
          await created.initialize()
        })
        finish()
      } catch (error) { finish(error) }
    })()
    return promise
  }

  private loadSdk(): Promise<typeof import('@paddleocr/paddleocr-js')> {
    return import('@paddleocr/paddleocr-js')
  }

  private async withPredictionTimeout(
    prediction: ReturnType<OcrEngineInstance['predict']>,
    engine: OcrEngineInstance,
    generation: number,
  ): Promise<Awaited<ReturnType<OcrEngineInstance['predict']>>> {
    let timeoutId: ReturnType<typeof setTimeout> | undefined
    const timeout = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(() => reject(new OcrPredictionTimeoutError(this.predictionTimeoutMs)), this.predictionTimeoutMs)
    })
    try {
      return await Promise.race([prediction, timeout])
    } catch (error) {
      if (error instanceof OcrPredictionTimeoutError) this.invalidateEngine(engine, generation)
      throw error
    } finally {
      if (timeoutId !== undefined) clearTimeout(timeoutId)
    }
  }

  private invalidateEngine(instance: OcrEngineInstance, generation: number): void {
    if (this.engine !== instance || this.generation !== generation) return
    performanceDiagnostics.increment('ocrResets')
    this.generation += 1
    this.engine = null
    this.initializing = null
    const resources = this.activeResources
    this.activeResources = null
    this.busyToken = null
    void this.queueDisposal(instance).finally(() => resources?.release())
  }

  private queueDisposal(instance: OcrEngineInstance): Promise<void> {
    const previous = this.pendingDisposal
    let disposal!: Promise<void>
    disposal = (async () => {
      await previous
      await this.safeDispose(instance)
    })().finally(() => {
      if (this.pendingDisposal === disposal) this.pendingDisposal = null
    })
    this.pendingDisposal = disposal
    return disposal
  }

  private async safeDispose(instance: OcrEngineInstance): Promise<void> {
    try {
      await instance.dispose()
    } catch {
      // A failed worker teardown must not prevent a fresh engine from starting.
    }
  }

  private toReading(result: OcrResult, fallbackDuration: number, scoreThreshold: number): OcrReading {
    const accepted = result.items.filter((item) => item.score >= scoreThreshold)
    const confidence = accepted.length
      ? accepted.reduce((sum, item) => sum + item.score, 0) / accepted.length
      : 0
    return {
      text: accepted.map((item) => item.text).join(' '),
      items: accepted.map((item) => ({
        text: item.text,
        confidence: item.score,
        polygon: item.poly,
      })),
      confidence,
      durationMs: result.metrics.totalMs || fallbackDuration,
      detectedItems: result.items.length,
      acceptedItems: accepted.length,
      bestConfidence: result.items.reduce((best, item) => Math.max(best, item.score), 0),
    }
  }
}

const sharedEngines = new Map<string, OcrEngine>()

export function getSharedOcrEngine(models: GamePreset['remoteResources']['ocrModels']): OcrEngine {
  const key = sharedOcrEngineKey(models)
  const existing = sharedEngines.get(key)
  if (existing) return existing
  const engine = new OcrEngine(models)
  sharedEngines.set(key, engine)
  return engine
}

export async function releaseSharedOcrEngine(models: GamePreset['remoteResources']['ocrModels']): Promise<void> {
  const key = sharedOcrEngineKey(models)
  const engine = sharedEngines.get(key)
  if (!engine) return
  sharedEngines.delete(key)
  await engine.dispose()
}

function sharedOcrEngineKey(models: GamePreset['remoteResources']['ocrModels']): string {
  return `${models.detection.sha256}|${models.recognition.sha256}`
}
